import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "../lib/content";

const LEFT_LINKS = NAV_LINKS.slice(0, 2); // Home, About
const RIGHT_LINKS = NAV_LINKS.slice(2);    // Services, Holistic Care, Contact

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const renderLink = (link, prefix = "nav-link") => {
    const isAnchor = link.href.includes("#");
    if (isAnchor) {
      return (
        <a
          key={link.href}
          href={link.href}
          data-testid={`${prefix}-${link.label.toLowerCase().replace(/\s/g, "-")}`}
          className="px-4 py-2 text-sm font-medium rounded-full text-[var(--at-navy)] hover:text-[var(--at-teal)] transition-colors"
        >
          {link.label}
        </a>
      );
    }
    return (
      <NavLink
        key={link.href}
        to={link.href}
        end={link.href === "/"}
        data-testid={`${prefix}-${link.label.toLowerCase().replace(/\s/g, "-")}`}
        className={({ isActive }) =>
          `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
            isActive
              ? "text-[var(--at-teal)]"
              : "text-[var(--at-navy)] hover:text-[var(--at-teal)]"
          }`
        }
      >
        {link.label}
      </NavLink>
    );
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[var(--at-bg)]/85 backdrop-blur-xl border-b border-[var(--at-border)]/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-36 md:h-40 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        {/* Left nav cluster (desktop) */}
        <nav className="hidden lg:flex items-center gap-1 justify-start">
          {LEFT_LINKS.map((l) => renderLink(l))}
        </nav>

        {/* Mobile: hamburger placeholder to keep grid balanced */}
        <div className="lg:hidden flex justify-start" />

        {/* Center Logo — larger, glossy, dynamic */}
        <Link
          to="/"
          data-testid="nav-logo-link"
          aria-label="All Thrive home"
          className="group relative flex items-center justify-center"
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -m-2 rounded-full bg-[radial-gradient(circle_at_30%_25%,rgba(255,255,255,0.85),rgba(234,242,242,0.45)_45%,transparent_70%)] opacity-90 transition-opacity duration-500 group-hover:opacity-100"
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-full bg-[var(--at-teal-tint)]/40 blur-xl scale-110 opacity-60 group-hover:opacity-90 transition-opacity duration-500"
          />
          <img
            src={BRAND.logo}
            alt="All Thrive Therapy and Consulting"
            className="relative h-[7.5rem] w-[7.5rem] md:h-[7.875rem] md:w-[7.875rem] object-contain drop-shadow-[0_10px_22px_rgba(26,46,68,0.2)] transition-all duration-500 ease-out group-hover:-translate-y-0.5 group-hover:drop-shadow-[0_16px_30px_rgba(42,123,118,0.34)] group-hover:rotate-[-1.5deg] logo-float"
          />
          {/* Specular sheen */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-full overflow-hidden"
          >
            <span className="logo-sheen absolute -inset-x-1/2 top-0 h-full w-1/2 bg-gradient-to-r from-transparent via-white/55 to-transparent skew-x-[-18deg]" />
          </span>
        </Link>

        {/* Right nav cluster + CTA (desktop) */}
        <div className="hidden lg:flex items-center justify-end gap-1">
          <nav className="flex items-center gap-1">
            {RIGHT_LINKS.map((l) => renderLink(l))}
          </nav>
          <Link
            to="/contact"
            data-testid="nav-cta-consultation"
            className="ml-3 btn-primary text-sm py-3 px-6"
          >
            Free Consultation
          </Link>
        </div>

        {/* Mobile toggle (right column) */}
        <div className="lg:hidden flex justify-end">
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="p-2.5 rounded-full border border-[var(--at-border)] bg-[var(--at-bg)] text-[var(--at-navy)]"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden bg-[var(--at-bg)] border-t border-[var(--at-border)] px-6 py-6"
        >
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => {
              const isAnchor = link.href.includes("#");
              if (isAnchor) {
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    data-testid={`nav-mobile-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="px-3 py-3 rounded-xl text-[var(--at-navy)] hover:bg-[var(--at-bg-2)] font-medium"
                  >
                    {link.label}
                  </a>
                );
              }
              return (
                <NavLink
                  key={link.href}
                  to={link.href}
                  end={link.href === "/"}
                  data-testid={`nav-mobile-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-xl font-medium ${
                      isActive
                        ? "bg-[var(--at-teal-tint)] text-[var(--at-teal)]"
                        : "text-[var(--at-navy)] hover:bg-[var(--at-bg-2)]"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              );
            })}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta"
              className="btn-primary mt-3"
            >
              Free Consultation
            </Link>
          </div>
        </div>
      )}

      <style>{`
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        .logo-float { animation: logoFloat 5.5s ease-in-out infinite; }

        @keyframes logoSheen {
          0% { transform: translateX(-120%) skewX(-18deg); opacity: 0; }
          18% { opacity: 0.85; }
          55% { transform: translateX(260%) skewX(-18deg); opacity: 0; }
          100% { transform: translateX(260%) skewX(-18deg); opacity: 0; }
        }
        .logo-sheen { animation: logoSheen 6s ease-in-out 1.2s infinite; }
      `}</style>
    </header>
  );
}
